import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { Alert, Button, Card, CardBody, Col, Container, Input, Label, Row, Spinner, Table } from "reactstrap";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList,
} from "recharts";
import { useAuth } from "auth/useAuth";
import { useUserGroups } from "../../shared/hooks/useUserGroups";
import { setupApiAuth } from "../../helpers/auth-helper";
import ApiService from "../../shared/services/apiService";
import "./dashboard.css";

// ─── Labels (pt-BR) ─────────────────────────────────────────────────────────

const STAGE = [
  { key: "accounts", label: "Contas criadas", color: "var(--viz-stage-1)" },
  { key: "confirmed", label: "Confirmadas", color: "var(--viz-stage-2)" },
  { key: "profiles", label: "Com perfil", color: "var(--viz-stage-3)" },
  { key: "curricula", label: "Com currículo", color: "var(--viz-stage-4)" },
];

const GENDER = { masculino: "Masculino", feminino: "Feminino", outros: "Outro", prefiro_nao_informar: "Prefiro não informar", nao_informado: "Não informado" };
const EDUCATION = { elementary: "Ensino Fundamental", highschool: "Ensino Médio", college: "Ensino Superior", graduate: "Pós-Graduação", nao_informado: "Não informado" };
const AGE = { menos_18: "Menos de 18", "18_24": "18 a 24", "25_34": "25 a 34", "35_44": "35 a 44", "45_54": "45 a 54", "55_mais": "55 ou mais", nao_informado: "Não informado" };
const AGE_ORDER = ["menos_18", "18_24", "25_34", "35_44", "45_54", "55_mais", "nao_informado"];

const fmtWeek = (iso) => {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}`;
};
const fmtDay = fmtWeek;
const pct = (num, den) => (den > 0 ? Math.round((num / den) * 100) : null);
const fmtPct = (v) => (v === null || v === undefined ? "—" : `${v}%`);

/** Top-N + "Outros", so a long tail never becomes a wall of bars. */
const topN = (rows, n, labelFn = (l) => l) => {
  const sorted = [...rows];
  const head = sorted.slice(0, n).map((r) => ({ label: labelFn(r.label), count: r.count }));
  const rest = sorted.slice(n).reduce((s, r) => s + r.count, 0);
  if (rest > 0) head.push({ label: "Outros", count: rest });
  return head;
};

// ─── Small building blocks ──────────────────────────────────────────────────

const StatTile = ({ label, value, sub }) => (
  <Card className="mb-3 h-100">
    <CardBody>
      <div className="viz-stat-label">{label}</div>
      <div className="viz-stat-value">{value}</div>
      {sub && <div className="viz-stat-sub mt-1">{sub}</div>}
    </CardBody>
  </Card>
);

const EmptyChart = ({ height = 260 }) => (
  <div className="d-flex align-items-center justify-content-center viz-stat-sub" style={{ height }}>Sem dados no período</div>
);

const ChartCard = ({ title, subtitle, action, children }) => (
  <Card className="mb-3">
    <CardBody>
      <div className="d-flex justify-content-between align-items-start mb-2">
        <div>
          <h5 className="mb-0">{title}</h5>
          {subtitle && <div className="viz-stat-sub">{subtitle}</div>}
        </div>
        {action}
      </div>
      {children}
    </CardBody>
  </Card>
);

/** Single-series horizontal bars, one hue, direct value labels (no legend needed). */
const DistributionChart = ({ title, rows }) => (
  <ChartCard title={title}>
    {rows.length === 0 ? (
      <div className="viz-stat-sub py-4 text-center">Sem dados</div>
    ) : (
      <ResponsiveContainer width="100%" height={Math.max(160, rows.length * 32)}>
        <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 32, bottom: 4, left: 8 }} barCategoryGap={6}>
          <XAxis type="number" hide />
          <YAxis type="category" dataKey="label" width={130} tickLine={false} axisLine={false} />
          <Tooltip cursor={{ fill: "var(--viz-ring)" }} formatter={(v) => [v, "Pessoas"]} />
          <Bar dataKey="count" fill="var(--viz-series-1)" radius={[0, 4, 4, 0]} maxBarSize={18} isAnimationActive={false}>
            <LabelList dataKey="count" position="right" style={{ fill: "var(--viz-text-2)", fontSize: 12 }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    )}
  </ChartCard>
);

// ─── Page ───────────────────────────────────────────────────────────────────

const Dashboard = () => {
  document.title = "Painel | ACIG Guapiaçu Cidadão";

  const auth = useAuth();
  const { isAdmin } = useUserGroups();
  const [weeks, setWeeks] = useState(12);
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [funnelAsTable, setFunnelAsTable] = useState(false);
  const [eventsAsTable, setEventsAsTable] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setupApiAuth(auth);
      const res = await ApiService.getAdminMetrics({ weeks, days });
      setData(res.data || res);
    } catch (err) {
      console.error("Error loading metrics:", err);
      setError(err.response?.status === 403 ? "Acesso restrito a administradores." : "Não foi possível carregar as métricas.");
    } finally {
      setLoading(false);
    }
  }, [auth, weeks, days]);

  useEffect(() => { if (auth.isAuthenticated && isAdmin) load(); }, [auth.isAuthenticated, isAdmin, weeks, days]);

  const weekly = useMemo(() => (data?.weekly || []).map((w) => ({
    ...w,
    label: fmtWeek(w.weekStart),
    confirmRate: pct(w.confirmed, w.accounts),
  })), [data]);

  const daily = useMemo(() => (data?.events?.daily || []).map((d) => ({
    ...d,
    label: fmtDay(d.date),
    iniciados: d.auth_signup_started,
    confirmados: d.auth_confirmed,
    retomados: d.auth_confirm_resumed,
  })), [data]);

  const dist = useMemo(() => {
    const d = data?.distributions || {};
    const byOrder = (rows, order, labels) =>
      order.filter((k) => (rows || []).some((r) => r.label === k))
        .map((k) => ({ label: labels[k], count: rows.find((r) => r.label === k).count }));
    return {
      city: topN(d.city || [], 8, (l) => (l === "nao_informado" ? "Não informado" : l)),
      gender: topN(d.gender || [], 5, (l) => GENDER[l] || l),
      education: topN(d.education || [], 5, (l) => EDUCATION[l] || l),
      ageBand: byOrder(d.ageBand, AGE_ORDER, AGE),
    };
  }, [data]);

  if (!auth.isLoading && auth.isAuthenticated && !isAdmin) return <Navigate to="/profile" replace />;

  const hasWeekly = weekly.some((w) => w.accounts > 0);
  const hasDaily = daily.some((d) => d.iniciados || d.confirmados || d.retomados);
  const t = data?.totals || {};
  const ev = data?.events?.totals || {};
  const confirmRate = pct(t.confirmed, t.accounts);
  const eventConfirmRate = pct(ev.auth_confirmed, ev.auth_signup_submitted);

  return (
    <div className="page-content viz-root">
      <Container fluid>
        <div className="d-flex flex-wrap justify-content-between align-items-end gap-2 mb-3">
          <div>
            <h4 className="mb-0">Painel</h4>
            <div className="viz-stat-sub">
              Funil de cadastro dos convidados. Equipe (admin e associados) não entra na conta.
              {data?.generatedAt && <> Atualizado às {new Date(data.generatedAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}.</>}
            </div>
          </div>
          <div className="d-flex align-items-end gap-2">
            <div>
              <Label className="form-label mb-1 viz-stat-sub">Semanas</Label>
              <Input type="select" bsSize="sm" value={weeks} onChange={(e) => setWeeks(Number(e.target.value))}>
                {[4, 8, 12, 26].map((w) => <option key={w} value={w}>Últimas {w}</option>)}
              </Input>
            </div>
            <div>
              <Label className="form-label mb-1 viz-stat-sub">Eventos</Label>
              <Input type="select" bsSize="sm" value={days} onChange={(e) => setDays(Number(e.target.value))}>
                {[7, 30, 90].map((d) => <option key={d} value={d}>Últimos {d} dias</option>)}
              </Input>
            </div>
            <Button color="primary" outline size="sm" onClick={load} disabled={loading}>
              <i className={`bx bx-refresh me-1 ${loading ? "bx-spin" : ""}`} />Atualizar
            </Button>
          </div>
        </div>

        {error && <Alert color="danger">{error}</Alert>}
        {loading && !data && <div className="text-center py-5"><Spinner color="primary" /></div>}

        {data && (
          <>
            {/* KPI row */}
            <Row>
              <Col sm={6} xl={3}><StatTile label="Contas criadas" value={t.accounts} sub={`${t.unconfirmed} ainda não confirmadas`} /></Col>
              <Col sm={6} xl={3}><StatTile label="Taxa de confirmação" value={fmtPct(confirmRate)} sub={`${t.confirmed} de ${t.accounts} contas`} /></Col>
              <Col sm={6} xl={3}><StatTile label="Perfis preenchidos" value={t.profiles} sub={fmtPct(pct(t.profiles, t.confirmed)) + " das confirmadas"} /></Col>
              <Col sm={6} xl={3}><StatTile label="Currículos" value={t.curricula} sub={fmtPct(pct(t.curricula, t.profiles)) + " dos perfis"} /></Col>
            </Row>

            {/* Funnel by cohort week */}
            <ChartCard
              title="Funil por semana de cadastro"
              subtitle="Cada coluna é uma semana; as barras mostram quantas contas criadas naquela semana avançaram em cada etapa."
              action={
                <Button color="secondary" outline size="sm" onClick={() => setFunnelAsTable((v) => !v)}>
                  <i className={`bx ${funnelAsTable ? "bx-bar-chart-alt-2" : "bx-table"} me-1`} />{funnelAsTable ? "Gráfico" : "Tabela"}
                </Button>
              }
            >
              {funnelAsTable ? (
                <Table size="sm" responsive className="viz-table mb-0">
                  <thead className="table-light">
                    <tr><th>Semana de</th>{STAGE.map((s) => <th key={s.key} className="text-end">{s.label}</th>)}<th className="text-end">Confirmação</th></tr>
                  </thead>
                  <tbody>
                    {weekly.map((w) => (
                      <tr key={w.weekStart}>
                        <td>{w.label}</td>
                        {STAGE.map((s) => <td key={s.key} className="text-end">{w[s.key]}</td>)}
                        <td className="text-end">{fmtPct(w.confirmRate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : !hasWeekly ? <EmptyChart height={300} /> : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={weekly} margin={{ top: 8, right: 8, bottom: 0, left: -16 }} barGap={2} barCategoryGap="30%">
                    <CartesianGrid vertical={false} />
                    <XAxis dataKey="label" tickLine={false} />
                    <YAxis allowDecimals={false} tickLine={false} axisLine={false} />
                    <Tooltip cursor={{ fill: "var(--viz-ring)" }} labelFormatter={(l) => `Semana de ${l}`} />
                    <Legend iconType="square" iconSize={10} />
                    {STAGE.map((s) => (
                      <Bar key={s.key} dataKey={s.key} name={s.label} fill={s.color} radius={[4, 4, 0, 0]} maxBarSize={22} isAnimationActive={false} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            <Row>
              {/* Confirmation rate trend — the one number the redesign targets */}
              <Col xl={6}>
                <ChartCard title="Taxa de confirmação por semana" subtitle="Contas confirmadas ÷ contas criadas na semana. É a métrica que mostra se o problema do código no celular acabou.">
                  {!hasWeekly ? <EmptyChart /> : (
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={weekly} margin={{ top: 12, right: 16, bottom: 0, left: -16 }}>
                      <CartesianGrid vertical={false} />
                      <XAxis dataKey="label" tickLine={false} />
                      <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tickLine={false} axisLine={false} />
                      <Tooltip formatter={(v, _n, p) => [v === null ? "—" : `${v}% (${p.payload.confirmed}/${p.payload.accounts})`, "Confirmação"]} labelFormatter={(l) => `Semana de ${l}`} />
                      <Line type="monotone" dataKey="confirmRate" name="Confirmação" stroke="var(--viz-series-1)" strokeWidth={2} dot={{ r: 4, strokeWidth: 2, fill: "var(--viz-surface)" }} activeDot={{ r: 6 }} connectNulls isAnimationActive={false} />
                    </LineChart>
                  </ResponsiveContainer>
                  )}
                </ChartCard>
              </Col>

              {/* Telemetry — daily auth funnel events */}
              <Col xl={6}>
                <ChartCard
                  title={`Eventos de cadastro (últimos ${data.window?.eventDays || days} dias)`}
                  subtitle={`Retomadas após recarregar: ${ev.auth_confirm_resumed || 0} · Reenvios de código: ${ev.auth_confirm_resend || 0} · Confirmação nos eventos: ${fmtPct(eventConfirmRate)}`}
                  action={
                    <Button color="secondary" outline size="sm" onClick={() => setEventsAsTable((v) => !v)}>
                      <i className={`bx ${eventsAsTable ? "bx-line-chart" : "bx-table"} me-1`} />{eventsAsTable ? "Gráfico" : "Tabela"}
                    </Button>
                  }
                >
                  {eventsAsTable ? (
                    <div style={{ maxHeight: 260, overflowY: "auto" }}>
                      <Table size="sm" responsive className="viz-table mb-0">
                        <thead className="table-light">
                          <tr><th>Dia</th><th className="text-end">Iniciados</th><th className="text-end">Confirmações</th><th className="text-end">Retomadas</th><th className="text-end">Reenvios</th></tr>
                        </thead>
                        <tbody>
                          {daily.map((d) => (
                            <tr key={d.date}>
                              <td>{d.label}</td>
                              <td className="text-end">{d.iniciados}</td>
                              <td className="text-end">{d.confirmados}</td>
                              <td className="text-end">{d.retomados}</td>
                              <td className="text-end">{d.auth_confirm_resend}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : !hasDaily ? <EmptyChart /> : (
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={daily} margin={{ top: 12, right: 16, bottom: 0, left: -16 }}>
                      <CartesianGrid vertical={false} />
                      <XAxis dataKey="label" tickLine={false} interval="preserveStartEnd" />
                      <YAxis allowDecimals={false} tickLine={false} axisLine={false} />
                      <Tooltip labelFormatter={(l) => `Dia ${l}`} />
                      <Legend iconType="plainline" />
                      <Line type="monotone" dataKey="iniciados" name="Cadastros iniciados" stroke="var(--viz-series-1)" strokeWidth={2} dot={false} activeDot={{ r: 5 }} isAnimationActive={false} />
                      <Line type="monotone" dataKey="confirmados" name="Confirmações" stroke="var(--viz-series-3)" strokeWidth={2} dot={false} activeDot={{ r: 5 }} isAnimationActive={false} />
                      <Line type="monotone" dataKey="retomados" name="Retomadas após recarregar" stroke="var(--viz-series-2)" strokeWidth={2} dot={false} activeDot={{ r: 5 }} isAnimationActive={false} />
                    </LineChart>
                  </ResponsiveContainer>
                  )}
                </ChartCard>
              </Col>
            </Row>

            {/* Distributions */}
            <Row>
              <Col md={6} xl={6}><DistributionChart title="Cidade" rows={dist.city} /></Col>
              <Col md={6} xl={6}><DistributionChart title="Faixa etária" rows={dist.ageBand} /></Col>
              <Col md={6} xl={6}><DistributionChart title="Gênero" rows={dist.gender} /></Col>
              <Col md={6} xl={6}><DistributionChart title="Escolaridade (com currículo)" rows={dist.education} /></Col>
            </Row>
          </>
        )}
      </Container>
    </div>
  );
};

export default Dashboard;
